
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Smartphone,
  Truck,
  Link,
  Unlink,
  PieChart as PieChartIcon,
  List,
  ChevronRight,
  Loader2,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import "../Style/DeviceAssociate.css";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

// --- API FETCH FUNCTIONS ---

const fetchCustomers = async () => {
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No authorization token found.");

  const response = await fetch(`${API_BASE_URL}/customers`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  if (!response.ok) throw new Error("Failed to fetch customers");
  return response.json();
};

const fetchDeviceData = async ({ queryKey }) => {
  const [, selectedCustomer] = queryKey;
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No authorization token found.");

  let url = `${API_BASE_URL}/deviceassociate`;
  if (selectedCustomer !== "all") {
    url += `?customerid=${selectedCustomer}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    const errData = await response.json();
    throw new Error(
      errData.Message || `HTTP error! status: ${response.status}`,
    );
  }

  const rawData = await response.json();

  const deviceEntries = rawData.associations.filter((a) => a.deviceid);
  const deviceList = deviceEntries.map((a) => ({
    id: a.status === "Associated" ? a.vehicleid : "N/A",
    name: a.deviceid,
    status: a.status,
  }));

  const associatedVehicles = deviceEntries
    .filter((a) => a.vehicleid)
    .map((a) => ({
      id: a.deviceid,
      name: a.vehicleid,
      status: "Associated",
    }));

  const unassociatedVehicleEntries = rawData.associations.filter(
    (a) => a.vehicleid && !a.deviceid,
  );
  const unassociatedVehicles = unassociatedVehicleEntries.map((a) => ({
    id: "N/A",
    name: a.vehicleid,
    status: "Unassociated",
  }));

  const vehicleMap = new Map();
  [...associatedVehicles, ...unassociatedVehicles].forEach((v) => {
    if (!vehicleMap.has(v.name)) vehicleMap.set(v.name, v);
  });

  return {
    ...rawData,
    deviceList: deviceList,
    vehicleList: Array.from(vehicleMap.values()),
  };
};

const fetchAssignedVehicles = async ({ queryKey }) => {
  const [, selectedCustomer] = queryKey;
  const token = localStorage.getItem("token");
  if (!token) throw new Error("No token found");

  let url = `${API_BASE_URL}/vehiclereg/assigned?page=1&per_page=100`;
  if (selectedCustomer !== "all") {
    url += `&customer_id=${selectedCustomer}`;
  }

  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// --- SUB-TABLE COMPONENTS ---

const AssociatedDevicesTable = ({
  items,
  onValidate,
  validationStatus,
  validatingDeviceId,
  isLoading,
}) => (
  <div className="report-table-container">
    <table
      className="report-table"
      style={{ width: "100%", textAlign: "left" }}
    >
      <thead>
        <tr>
          <th>Vehicle Number</th>
          <th>Device Id</th>
          <th>IMEI</th>
          <th>VIN</th>
          <th>ICCID</th>
          <th style={{ textAlign: "center" }}>Validate</th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Loader2 className="animate-spin" size={18} /> Loading assigned
                assets...
              </div>
            </td>
          </tr>
        ) : items.length > 0 ? (
          items.map((item) => {
            const rowId = item.UniqueDeviceId;
            const rowStatus = validationStatus[rowId] || {};

            // FIX: Ensure rowId exists so we don't accidentally match null === null
            const isCurrentValidating = Boolean(
              rowId && validatingDeviceId === rowId,
            );

            return (
              <tr key={item.VehicleNumber}>
                <td>{item.VehicleNumber || "-"}</td>
                <td>{rowId || "N/A"}</td>
                <td>{item.IMEI || "-"}</td>
                <td>{item.VIMNumber || "-"}</td>
                <td>{item.ICCID || "-"}</td>
                <td style={{ textAlign: "center", minWidth: "165px" }}>
                  {rowStatus.status !== "success" &&
                  rowStatus.status !== "updated" ? (
                    <button
                      className="validate-btn"
                      disabled={!rowId || isCurrentValidating}
                      onClick={() =>
                        onValidate(rowId, rowStatus.status === "mismatch")
                      }
                      style={{
                        padding: "6px 14px",
                        backgroundColor:
                          rowStatus.status === "mismatch"
                            ? "#EF4444"
                            : "#3B82F6",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: rowId ? "pointer" : "not-allowed",
                        fontWeight: "500",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        opacity: rowId ? 1 : 0.6,
                      }}
                    >
                      {/* The spinner will only show if it is TRULY validating a valid device ID */}
                      {isCurrentValidating && (
                        <Loader2 className="animate-spin" size={14} />
                      )}
                      {rowStatus.status === "mismatch" ? "Update" : "Validate"}
                    </button>
                  ) : (
                    <span
                      style={{
                        color: "#10B981",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      <CheckCircle size={16} /> Done
                    </span>
                  )}

                  {rowStatus.message && (
                    <div
                      style={{
                        fontSize: "11px",
                        marginTop: "6px",
                        color:
                          rowStatus.status === "mismatch" ||
                          rowStatus.status === "error"
                            ? "#EF4444"
                            : "#10B981",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      {rowStatus.status === "mismatch" && (
                        <AlertTriangle size={12} />
                      )}
                      {rowStatus.message}
                    </div>
                  )}
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td
              colSpan="6"
              className="no-data"
              style={{ textAlign: "center", padding: "20px" }}
            >
              No Associated Devices Found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const UnassociatedDevicesTable = ({ items, vehicleList }) => {
  const navigate = useNavigate();
  return (
    <div className="report-table-container">
      <table className="report-table">
        <thead>
          <tr>
            <th>Device Id</th>
            <th>Vehicle Id</th>
            <th>Associate</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.name}>
              <td>{item.name}</td>
              <td>
                <select className="vehicle-dropdown">
                  <option value="">Select Vehicle</option>
                  {vehicleList.map((vehicle) => (
                    <option key={vehicle.name} value={vehicle.name}>
                      {vehicle.name}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <button
                  className="associate-btn"
                  onClick={() =>
                    navigate("/ParameterOverAir", {
                      state: { deviceId: item.name, vehicleId: item.id },
                    })
                  }
                >
                  Associate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- STATS CARD & CHARTS PLUGINS ---
const StatCard = ({
  title,
  value,
  icon,
  color,
  linkText,
  onClick = () => {},
}) => {
  const IconComponent = icon;
  const fgColorClass = `text-${color}-500-fg-deviceassociate`;
  const bgColorClass = `text-${color}-500-bg-deviceassociate`;

  return (
    <div className="stat-card-deviceassociate">
      <div>
        <div className="stat-card-header-deviceassociate">
          <div
            className={`stat-card-icon-wrapper-deviceassociate ${bgColorClass}`}
          >
            <IconComponent
              className={`stat-card-icon-deviceassociate ${fgColorClass}`}
            />
          </div>
          <span className="stat-card-label-deviceassociate">Monthly</span>
        </div>
        <div className="stat-card-body-deviceassociate">
          <h3 className="stat-card-value-deviceassociate">{value}</h3>
          <p className="stat-card-title-deviceassociate">{title}</p>
        </div>
      </div>
      {linkText && (
        <a
          href="/"
          className={`stat-card-link-deviceassociate ${fgColorClass}`}
          onClick={(e) => {
            e.preventDefault();
            onClick();
          }}
        >
          {linkText}
          <ChevronRight className="stat-card-link-icon-deviceassociate" />
        </a>
      )}
    </div>
  );
};

const PieChart = ({ data }) => {
  const colors = { Associated: "#6366F1", Unassociated: "#F97316" };
  const total = data.reduce((acc, item) => acc + item.value, 0);
  let cumulativePercent = 0;
  const gradients = data.map((item) => {
    const percent = total > 0 ? (item.value / total) * 100 : 0;
    const start = cumulativePercent;
    cumulativePercent += percent;
    return `${colors[item.name]} ${start}% ${cumulativePercent}%`;
  });

  return (
    <div className="pie-chart-container-deviceassociate">
      <h4 className="chart-title-deviceassociate pie-chart-title-deviceassociate">
        Device Status
      </h4>
      <div className="pie-chart-flex-wrapper-deviceassociate">
        <div
          className="pie-chart-viz-deviceassociate"
          style={{ background: `conic-gradient(${gradients.join(", ")})` }}
        ></div>
        <div className="pie-chart-legend-deviceassociate">
          {data.map((item) => (
            <div
              key={item.name}
              className="pie-chart-legend-item-deviceassociate"
            >
              <div
                className="pie-chart-legend-square-deviceassociate"
                style={{ backgroundColor: colors[item.name] }}
              ></div>
              <div className="pie-chart-legend-text-deviceassociate">
                <span className="pie-chart-legend-name-deviceassociate">
                  {item.name}
                </span>
                <span className="pie-chart-legend-value-deviceassociate">
                  ({item.value.toLocaleString()} |{" "}
                  {total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0"}
                  %)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const VehicleBarChart = ({ data }) => {
  const colors = {
    Associated: "bar-fill-green-deviceassociate",
    Unassociated: "bar-fill-gray-deviceassociate",
  };
  const total = data.reduce((acc, item) => acc + item.value, 0);

  return (
    <div className="bar-chart-container-deviceassociate">
      <h4 className="chart-title-deviceassociate bar-chart-title-deviceassociate">
        Vehicle Status
      </h4>
      <div className="bar-chart-wrapper-deviceassociate">
        {data.map((item) => {
          const percent = total > 0 ? (item.value / total) * 100 : 0;
          return (
            <div key={item.name} className="bar-chart-item-deviceassociate">
              <div className="bar-chart-item-header-deviceassociate">
                <span className="bar-chart-item-name-deviceassociate">
                  {item.name}
                </span>
                <span className="bar-chart-item-value-deviceassociate">
                  {item.value.toLocaleString()} ({percent.toFixed(1)}%)
                </span>
              </div>
              <div className="bar-chart-track-deviceassociate">
                <div
                  className={`bar-chart-fill-deviceassociate ${colors[item.name]}`}
                  style={{ width: `${percent}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DetailedList = ({ items, title, icon }) => {
  const IconComponent = icon;
  return (
    <div className="list-container-deviceassociate">
      <h4 className="chart-title-deviceassociate list-title-deviceassociate">
        {title}
      </h4>
      <ul className="list-ul-deviceassociate">
        {items.map((item) => (
          <li key={item.name} className="list-item-deviceassociate">
            <div className="list-item-content-deviceassociate">
              <IconComponent className="list-item-icon-deviceassociate" />
              <div className="list-item-text-wrapper-deviceassociate">
                <p className="list-item-name-deviceassociate">{item.name}</p>
                <p className="list-item-id-deviceassociate">{item.id}</p>
              </div>
            </div>
            <span
              className={`list-item-status-deviceassociate status-${item.status.toLowerCase()}-deviceassociate`}
            >
              {item.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// --- SKELETONS & ERROR UI ---
const StatCardSkeleton = () => (
  <div className="stat-card-deviceassociate">
    <div
      className="skeleton-deviceassociate"
      style={{ width: "40px", height: "40px", borderRadius: "8px" }}
    ></div>
    <div
      className="skeleton-deviceassociate"
      style={{ width: "60%", height: "24px", marginTop: "12px" }}
    ></div>
  </div>
);

const ChartGridSkeleton = () => (
  <div
    className="dashboard-chart-grid-deviceassociate"
    style={{ display: "flex", gap: "20px" }}
  >
    <div
      style={{ flex: 1, height: "200px", background: "#f3f4f6" }}
      className="skeleton-deviceassociate"
    ></div>
    <div
      style={{ flex: 1, height: "200px", background: "#f3f4f6" }}
      className="skeleton-deviceassociate"
    ></div>
  </div>
);

const ErrorDisplay = ({ error, onRetry }) => (
  <div
    className="dashboard-error-deviceassociate"
    style={{ padding: "20px", textAlign: "center", color: "#EF4444" }}
  >
    <h3>Error Loading Dashboard</h3>
    <p>{error?.message || "An unknown error occurred."}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{ marginTop: "10px", padding: "6px 12px", cursor: "pointer" }}
      >
        Try Again
      </button>
    )}
  </div>
);

// --- MAIN DASHBOARD COMPONENT ---
export default function Deviceassociate() {
  const [activeList, setActiveList] = useState("chart");
  const [selectedCustomer, setSelectedCustomer] = useState("all");

  const [validationStatus, setValidationStatus] = useState({});
  const [validatingDeviceId, setValidatingDeviceId] = useState(null);

  // Query 1: Customers
  const { data: customerList = [], isLoading: isCustomerListLoading } =
    useQuery({
      queryKey: ["customers"],
      queryFn: fetchCustomers,
      staleTime: 1000 * 60 * 5,
    });

  // Query 2: Core Device Metadata
  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchDevices,
  } = useQuery({
    queryKey: ["deviceData", selectedCustomer],
    queryFn: fetchDeviceData,
    staleTime: 1000 * 60,
    enabled: !!localStorage.getItem("token"),
  });

  // Query 3: Assigned Table Data
  const {
    data: assignedVehiclesData,
    isLoading: isAssignedLoading,
    refetch: refetchAssigned,
  } = useQuery({
    queryKey: ["assignedVehicles", selectedCustomer],
    queryFn: fetchAssignedVehicles,
    staleTime: 1000 * 30,
    enabled:
      activeList === "associated_devices" && !!localStorage.getItem("token"),
  });

  const handleValidateVin = async (deviceId, isMismatchActive) => {
    if (!deviceId) return;
    setValidatingDeviceId(deviceId);
    const token = localStorage.getItem("token");

    try {
      const payload = { deviceid: deviceId };
      if (isMismatchActive) payload.confirm_update = true;

      const res = await axios.post(`${API_BASE_URL}/validate-vin`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status === "mismatch") {
        setValidationStatus((prev) => ({
          ...prev,
          [deviceId]: {
            status: "mismatch",
            message: "VIN mismatch. Please update",
          },
        }));
      } else if (res.data.status === "updated") {
        setValidationStatus((prev) => ({
          ...prev,
          [deviceId]: {
            status: "updated",
            message: "VIN updated successfully.",
          },
        }));
        refetchAssigned();
        refetchDevices();
      } else {
        setValidationStatus((prev) => ({
          ...prev,
          [deviceId]: {
            status: "success",
            message: "VIN validated successfully",
          },
        }));
      }
    } catch (err) {
      setValidationStatus((prev) => ({
        ...prev,
        [deviceId]: {
          status: "error",
          message: err.response?.data?.message || "Validation profile failed",
        },
      }));
    } finally {
      setValidatingDeviceId(null);
    }
  };

  let listProps = { items: [], title: "", icon: Smartphone };
  const viewMode = activeList === "chart" ? "chart" : "list";
  const unassociatedVehicles =
    data?.vehicleList?.filter((v) => v.status === "Unassociated") || [];

  if (data) {
    if (activeList === "all_devices") {
      listProps = {
        items: data.deviceList,
        title: "All Devices",
        icon: Smartphone,
      };
    } else if (activeList === "all_vehicles") {
      listProps = {
        items: data.vehicleList,
        title: "All Vehicles",
        icon: Truck,
      };
    }
  }

  return (
    <div className="dashboard-container-deviceassociate">
      <header className="dashboard-header-deviceassociate">
        <div className="header-top-row-deviceassociate">
          <div className="customer-filter-container-deviceassociate">
            <label
              htmlFor="customer-filter"
              className="customer-filter-label-deviceassociate"
            >
              Customer:
            </label>
            <select
              id="customer-filter"
              className="customer-filter-select-deviceassociate"
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              disabled={isCustomerListLoading}
            >
              <option value="all">All Customers</option>
              {customerList.map((customer) => (
                <option key={customer.CustomerID} value={customer.CustomerID}>
                  {customer.CustomerName}
                </option>
              ))}
            </select>
          </div>
        </div>
        <hr className="line-deviceassociate" width="100%" />
      </header>

      {/* Cards Metric Row */}
      <div className="dashboard-stats-grid-deviceassociate">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : isError ? (
          <ErrorDisplay error={error} onRetry={refetchDevices} />
        ) : (
          <>
            <StatCard
              title="Total Devices"
              value={data.totalDevices}
              icon={Smartphone}
              color="blue"
              linkText="View all devices"
              onClick={() => setActiveList("all_devices")}
            />
            <StatCard
              title="Total Vehicles"
              value={data.totalVehicles}
              icon={Truck}
              color="green"
              linkText="View all vehicles"
              onClick={() => setActiveList("all_vehicles")}
            />
            <StatCard
              title="Associated Devices"
              value={data.associatedDevices}
              icon={Link}
              color="indigo"
              linkText="View associations"
              onClick={() => setActiveList("associated_devices")}
            />
            <StatCard
              title="Unassociated Devices"
              value={data.unassociatedDevices}
              icon={Unlink}
              color="orange"
              linkText="View unlinked"
              onClick={() => setActiveList("unassociated_devices")}
            />
          </>
        )}
      </div>

      {/* Detailed Table & Dynamic Breakdown View Panel */}
      <div className="dashboard-breakdown-section-deviceassociate">
        <div className="dashboard-breakdown-header-deviceassociate">
          <h3 className="dashboard-breakdown-title-deviceassociate">
            Detailed Breakdown
          </h3>
          <div className="dashboard-toggle-wrapper-deviceassociate">
            <button
              onClick={() => setActiveList("chart")}
              className={`dashboard-toggle-button-deviceassociate ${viewMode === "chart" ? "dashboard-toggle-button-active-deviceassociate" : ""}`}
              disabled={isLoading}
            >
              <PieChartIcon className="dashboard-toggle-icon-deviceassociate" />
            </button>
            <button
              onClick={() => setActiveList("all_devices")}
              className={`dashboard-toggle-button-deviceassociate ${viewMode === "list" ? "dashboard-toggle-button-active-deviceassociate" : ""}`}
              disabled={isLoading}
            >
              <List className="dashboard-toggle-icon-deviceassociate" />
            </button>
          </div>
        </div>

        <div className="dashboard-breakdown-content-deviceassociate">
          {isLoading ? (
            <ChartGridSkeleton />
          ) : data ? (
            activeList === "all_devices" || activeList === "all_vehicles" ? (
              <DetailedList {...listProps} />
            ) : activeList === "associated_devices" ? (
              <AssociatedDevicesTable
                items={assignedVehiclesData?.Vehicle || []}
                isLoading={isAssignedLoading}
                onValidate={handleValidateVin}
                validationStatus={validationStatus}
                validatingDeviceId={validatingDeviceId}
              />
            ) : activeList === "unassociated_devices" ? (
              <UnassociatedDevicesTable
                items={data.deviceList.filter(
                  (d) => d.status === "Unassociated",
                )}
                vehicleList={unassociatedVehicles}
              />
            ) : (
              <div className="dashboard-chart-grid-deviceassociate">
                <PieChart data={data.chartData} />
                <VehicleBarChart data={data.vehicleChartData} />
              </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
